import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { BackButton } from '@/components/ui/back-button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authApi } from '@/lib/pillarApi';

function PasswordContent() {
    const navigate = useNavigate();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [error, setError] = useState<string | null>(null);

    const changePasswordMutation = useMutation({
        mutationFn: authApi.changePassword,
        onSuccess: () => {
            setSuccessMessage('Password changed successfully!');
            setError(null);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setTimeout(() => setSuccessMessage(''), 5000);
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccessMessage('');

        if (newPassword !== confirmPassword) {
            setError('New password and confirmation do not match');
            return;
        }

        if (newPassword.length < 8) {
            setError('New password must be at least 8 characters');
            return;
        }

        changePasswordMutation.mutate({
            old_password: currentPassword,
            new_password: newPassword,
        });
    };

    return (
        <div>
            <BackButton href="/profile" tooltip="Back to Profile" />
            <div className="flex items-center gap-4 mb-8">
                <h1 className="text-3xl font-bold">Change Password</h1>
            </div>

            <div className="max-w-2xl">
                <Card>
                    <CardHeader>
                        <CardTitle>Update Your Password</CardTitle>
                        <CardDescription>
                            Choose a strong password to protect your account
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {successMessage && (
                            <Alert className="mb-6 bg-green-50 border-green-200">
                                <AlertDescription className="text-green-800">
                                    {successMessage}
                                </AlertDescription>
                            </Alert>
                        )}

                        {(error || changePasswordMutation.error) && (
                            <Alert variant="destructive" className="mb-6">
                                <AlertDescription>
                                    {error ||
                                        (changePasswordMutation.error instanceof Error
                                            ? changePasswordMutation.error.message
                                            : 'Failed to change password')}
                                </AlertDescription>
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="space-y-2">
                                <Label htmlFor="currentPassword">Current Password</Label>
                                <Input
                                    id="currentPassword"
                                    type="password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    required
                                    disabled={changePasswordMutation.isPending}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="newPassword">New Password</Label>
                                <Input
                                    id="newPassword"
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                    minLength={8}
                                    disabled={changePasswordMutation.isPending}
                                />
                                <p className="text-sm text-muted-foreground">
                                    Must be at least 8 characters long
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    minLength={8}
                                    disabled={changePasswordMutation.isPending}
                                />
                            </div>

                            <div className="flex gap-4">
                                <Button
                                    type="submit"
                                    disabled={
                                        changePasswordMutation.isPending ||
                                        !currentPassword ||
                                        !newPassword ||
                                        !confirmPassword
                                    }
                                >
                                    {changePasswordMutation.isPending
                                        ? 'Changing Password...'
                                        : 'Change Password'}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => navigate('/profile')}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </form>

                        <div className="mt-8 p-4 bg-muted rounded-lg">
                            <h3 className="font-semibold mb-2">Password Requirements</h3>
                            <ul className="text-sm text-muted-foreground space-y-1">
                                <li>• Minimum 8 characters</li>
                                <li>• Use a mix of letters, numbers, and symbols</li>
                                <li>• Avoid common words or personal information</li>
                                <li>• Don't reuse passwords from other accounts</li>
                            </ul>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

export default function PasswordPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <PasswordContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
